/* eslint-disable no-param-reassign */
import produce from 'immer';
import objFromArray from 'src/utils/objFromArray';
import helpers from 'src/helpers';
import { sortChatMessages } from 'src/helpers/chatCursor';
/**
 * Updates messages in both the main chat and the filtered chat (if they exist).
 * Chats are filtered by phone number on the backend, and this filtering does not affect the main chat list.
 * As a result, filtered chat is stored separately and must be updated independently, alongside the main chat.
 *
 * @param {Object} draft - The current state being modified using Immer.
 * @param {string} chatKey - The key of the chat to search for in `draft.chats.byKey` and `draft.filteredChats.byKey`.
 * @param {Function} updateFn - The function that is applied to the chat (or filtered chat) to update its messages.
 *
 * This function searches for the chat by `chatKey` in two places:
 * - The main chat list (`draft.chats.byKey`).
 * - The filtered chat list (`draft.filteredChats.byKey`), if it exists.
 *
 * If a chat is found in either of these lists, the `updateFn` function is applied to it to update its data.
 */
const updateChatMessages = (draft, chatKey, updateFn) => {
  const chat = draft?.chats?.byKey?.[chatKey];
  const filteredChat = draft?.filteredChats?.byKey?.[chatKey];

  if (chat) {
    draft.chats.byKey[chatKey] = produce(chat, updateFn);
  }

  if (filteredChat) {
    draft.filteredChats.byKey[chatKey] = produce(filteredChat, updateFn);
  }
};

const updateChatMessagesProcessFnAddNew = (messages, scheduledMessages) => (draft) => {
  if (messages.length) {
    messages.forEach((message) => {
      if (!draft.messages.find((item) => item?.id && item.id === message?.id)) {
        draft.messages.push(message);
        // Only increment unread count for received messages that aren't already read
        // (auto-read keyword replies arrive with readAt already set)
        if (message?.isReceived && !message?.readAt) {
          const unreadCount = helpers.transform.toInt10(draft.unreadCount) + 1;
          draft.unreadCount = unreadCount > 0 ? unreadCount : 0;
        }
      }
    });
    // The thread is ordered by when the contact got each message (CON-1805);
    // a row that arrives over the socket must land where the next fetch
    // would put it, not simply at the end.
    sortChatMessages(draft.messages);
  }
  if (scheduledMessages.length) {
    scheduledMessages.forEach((scheduledMessage) => {
      if (!draft.scheduledMessages.find((item) => item?.id && item.id === scheduledMessage?.id)) {
        draft.scheduledMessages.push(scheduledMessage);
      }
    });
    draft.scheduledNextCursor = draft.scheduledMessages.at(-1)?.scheduledAt;
    if (draft.scheduledMessages.length > 1) {
      draft.scheduledMessages.sort((a, b) => new Date(a?.scheduledAt || 0) - new Date(b?.scheduledAt || 0));
    }
  }
};

const updateChatMessagesProcessFnDelete = (messageId, isScheduledMessage, updates) => (draft) => {
  if (isScheduledMessage) {
    draft.scheduledMessages = draft.scheduledMessages.filter((message) => message.id !== messageId);
  } else {
    draft.messages = draft.messages.map((message) => (
      updates[message.id] ? { ...message, ...updates[message.id] } : message
    ));
  }
};

const updateChatMessagesProcessFnUpdate = (messageId, updates) => (draft) => {
  const messageExists = draft.messages.some((message) => message.id === messageId);

  if (!messageExists && updates[messageId]) {
    const scheduledMessageIndex = draft.scheduledMessages?.findIndex((message) => message.id === messageId);

    if (scheduledMessageIndex !== undefined && scheduledMessageIndex >= 0) {
      const [scheduledMessage] = draft.scheduledMessages.splice(scheduledMessageIndex, 1);
      draft.messages.push({ ...scheduledMessage, ...updates[messageId] });
    } else {
      draft.messages.push(updates[messageId]);
    }
  } else {
    draft.messages = draft.messages.map((message) => {
      console.log(message);
      return (
        updates[message.id] ? { ...message, ...updates[message.id] } : message
      );
    });
  }
  // A queued row that just sent gains a sentAt and moves to it (CON-1805).
  sortChatMessages(draft.messages);
};

const updateChatScheduledMessagesProcessFnUpdate = (isSendNow, updatedMessage) => (draft) => {
  if (isSendNow) {
    draft.scheduledMessages = draft.scheduledMessages.filter((message) => message.id !== updatedMessage?.id);
    draft.messages.push(updatedMessage);
    sortChatMessages(draft.messages);
  } else {
    const messageExists = draft.scheduledMessages.some((message) => message.id === updatedMessage?.id);

    if (messageExists) {
      draft.scheduledMessages = draft.scheduledMessages.map((message) => (
        message.id === updatedMessage?.id ? updatedMessage : message
      ));
    } else {
      draft.scheduledMessages.push(updatedMessage);
    }

    if (draft.scheduledMessages.length > 1) {
      draft.scheduledMessages.sort((a, b) => new Date(a?.scheduledAt || 0) - new Date(b?.scheduledAt || 0));
    }
  }
};

const addChatMessagesIfNotExistingChat = (draft, payloadOptions) => {
  const {
    chatKey: chatKeyOption,
    messages,
    scheduledMessages,
    receivedMessagesCount,
    contact,
  } = payloadOptions || {};
  const contactId = contact?.id;
  const chatKey = chatKeyOption || contactId;

  if (!chatKey || !contactId) {
    return;
  }

  const existingChat = {
    key: chatKey,
    messages,
    scheduledMessages,
    participantIds: [contactId],
    unreadCount: receivedMessagesCount || 0,
    isPartialFromSocket: true,
  };

  draft.chats.byKey[chatKey] = existingChat;
  draft.chats.allKeys.push(String(chatKey));
  if (contact?.id) {
    draft.chatsContacts.allIds.push(contact.id);
    draft.chatsContacts.byId[contact.id] = contact;
  }
  if (draft.messagesFilteredByPhone) {
    draft.filteredChats.byKey[chatKey] = existingChat;
  }
};

const socketChatNewMessagePrepare = (messages, isSocketEventChatNewMessage = false) => {
  if (!isSocketEventChatNewMessage) {
    return messages;
  }

  return (messages || []).map((message) => ({
    ...message || {},
    ...message?.isReceived && !message?.readAt && message?.receivedAt ? {
      receivedAtIntSocket: new Date(message.receivedAt).getTime(),
    } : {},
  }));
};

/* ********************************************************************************** */

export const getMoreChatMessagesSuccessHandlers = (state, payload) => {
  const {
    contactId,
    contact,
    data,
  } = payload;

  const { messages = [], nextCursor = null } = data || {};

  return produce(state, (draft) => {
    const chatKey = String(contactId);

    // If chat doesn't exist, create it using the existing helper function
    if (!draft?.chats?.byKey?.[chatKey]) {
      addChatMessagesIfNotExistingChat(draft, {
        chatKey,
        messages,
        scheduledMessages: [],
        receivedMessagesCount: 0,
        contact: contact?.id ? {
          ...contact
        } : {
          id: contactId,
        },
      });
      // Set the cursor after creation
      draft.chats.byKey[chatKey].nextCursor = nextCursor;
    } else {
      // Chat exists, update it normally
      updateChatMessages(draft, contactId, (chat) => {
        if (chat.isPartialFromSocket) {
          // Replace messages for partial socket chats (avoids duplicates)
          chat.messages = messages;
        } else {
          chat.messages.unshift(...messages);
        }
        chat.nextCursor = nextCursor;
        chat.isPartialFromSocket = false;
      });
    }

    draft.isMessagesLoading = false;
  });
};

export const addMessageSuccessHandlers = (state, payload) => {
  const {
    chatKey,
    messages,
    scheduledMessages,
    // contact,
  } = payload;

  return produce(state, (draft) => {
    if (!draft?.chats?.byKey?.[chatKey]) {
      addChatMessagesIfNotExistingChat(draft, payload);
    } else {
      updateChatMessages(draft, chatKey, updateChatMessagesProcessFnAddNew(messages, scheduledMessages));
    }
    draft.isMessageSending = false;
  });
};

export const socketChatNewMessageHandlers = (state, payload) => produce(state, (draft) => {
  const {
    contacts,
    isSocketEventChatNewMessage,
  } = payload || {};
  const newPayload = Array.isArray(contacts) ? contacts : [];

  // eslint-disable-next-line no-restricted-syntax
  for (const item of newPayload) {
    const {
      id: chatKey,
      messages: messagesList,
      ...restOfContact
    } = item || {};

    const messages = socketChatNewMessagePrepare(
      Array.isArray(messagesList) ? messagesList.filter(({ isScheduled }) => !isScheduled) : [],
      isSocketEventChatNewMessage,
    );
    const scheduledMessages = Array.isArray(messagesList) ? messagesList.filter(({ isScheduled }) => !!isScheduled) : [];
    const receivedMessagesCount = Array.isArray(messagesList) ? messagesList.filter(({ isReceived }) => !!isReceived).length : 0;

    if (!draft?.chats?.byKey?.[chatKey]) {
      addChatMessagesIfNotExistingChat(
        draft,
        {
          chatKey,
          messages,
          scheduledMessages,
          receivedMessagesCount,
          contact: {
            ...restOfContact,
            id: chatKey,
          },
        }
      );
    } else {
      updateChatMessages(draft, chatKey, updateChatMessagesProcessFnAddNew(messages, scheduledMessages));
    }

    // Skip reorder only if ALL messages are auto-read keyword replies (isReceived with readAt set)
    const allMessagesAutoRead = messages.length > 0 && messages.every((msg) => msg?.isReceived && msg?.readAt);

    const chat = draft.chats.byKey[chatKey];
    if (chat) {
      if (!allMessagesAutoRead) {
        chat.socketDateReceived = new Date().toISOString();
        const currentIndex = draft.chats.allKeys.indexOf(String(chatKey));
        if (currentIndex > -1) {
          draft.chats.allKeys.splice(currentIndex, 1);
        }
        draft.chats.allKeys.unshift(String(chatKey));
      }
    }
  }
});

export const socketChatMessageDeleteHandlers = (state, payload) => produce(state, (draft) => {
  const newPayload = Array.isArray(payload?.contacts) ? payload.contacts : [];

  // eslint-disable-next-line no-restricted-syntax
  for (const item of newPayload) {
    const {
      id: chatKey,
      messages,
    } = item || {};
    const updates = objFromArray(messages);

    // eslint-disable-next-line no-restricted-syntax
    for (const message of messages) {
      const {
        id: messageId,
        isScheduled,
      } = message;
      updateChatMessages(draft, chatKey, updateChatMessagesProcessFnDelete(messageId, isScheduled, updates));
    }
  }
});

export const socketChatScheduledMessageDeleteHandlers = (state, payload) => produce(state, (draft) => {
  const newPayload = Array.isArray(payload?.contacts) ? payload.contacts : [];

  newPayload.forEach(({
    id: chatKey,
    messages,
  } = {}) => {
    if (!messages?.length) {
      return;
    }
    messages.forEach((messageId) => {
      updateChatMessages(draft, chatKey, updateChatMessagesProcessFnDelete(messageId, true, []));
    });
  });
});

export const sendNowOrEditScheduledMessageSuccessHandlers = (state, payload) => {
  const {
    chatKey,
    isSendNow,
    updatedMessage,
  } = payload;

  return produce(state, (draft) => {
    updateChatMessages(draft, chatKey, updateChatScheduledMessagesProcessFnUpdate(isSendNow, updatedMessage));
    draft.isMessageSending = false;
  });
};

export const socketChatScheduledMessageUpdateHandlers = (state, payload) => produce(state, (draft) => {
  const newPayload = Array.isArray(payload?.contacts) ? payload.contacts : [];

  // eslint-disable-next-line no-restricted-syntax
  for (const item of newPayload) {
    const {
      id: chatKey,
      messages,
    } = item || {};

    // eslint-disable-next-line no-restricted-syntax
    for (const message of messages) {
      const {
        isScheduled,
      } = message;
      updateChatMessages(draft, chatKey, updateChatScheduledMessagesProcessFnUpdate(!isScheduled, message));
    }
  }
});

export const socketChatMessageUpdateHandlers = (state, payload) => produce(state, (draft) => {
  const {
    contacts,
    isMessageReadStatusChanged,
  } = payload || {};
  const newPayload = Array.isArray(contacts) ? contacts : [];

  // eslint-disable-next-line no-restricted-syntax
  for (const item of newPayload) {
    const {
      id: chatKey,
      messages,
    } = item || {};
    const updates = objFromArray(messages);

    if (isMessageReadStatusChanged) {
      const isMessageRead = Array.isArray(messages) ? !!messages[0]?.readAt : false;
      const messagesCount = Array.isArray(messages) ? messages.length : 0;
      updateChatMessages(draft, chatKey, (draftChat) => {
        const unreadCount = isMessageRead
          ? helpers.transform.toInt10(draftChat.unreadCount) - messagesCount
          : helpers.transform.toInt10(draftChat.unreadCount) + messagesCount;
        draftChat.unreadCount = unreadCount >= 0 ? unreadCount : 0;
      });
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const message of messages) {
      const {
        id: messageId,
        // isScheduled,
      } = message;
      updateChatMessages(draft, chatKey, updateChatMessagesProcessFnUpdate(messageId, updates));
    }
  }
});

export const socketChatDeletedHandlers = (state, payload) => produce(state, (draft) => {
  const {
    contactId: chatKey
  } = payload || {};
  const chatKeyStr = String(chatKey);
  draft.deletedChatKey = chatKeyStr;
  draft.chats.allKeys = draft.chats.allKeys.filter((key) => key !== chatKeyStr);
  delete draft.chats.byKey[chatKey];
});

export const deleteChatMessageSuccessHandlers = (state, payload) => {
  const {
    chatKey,
    messageId,
    isScheduledMessage,
    updatedMessages
  } = payload;
  const updates = objFromArray(updatedMessages);

  return produce(state, (draft) => {
    updateChatMessages(draft, chatKey, updateChatMessagesProcessFnDelete(messageId, isScheduledMessage, updates));
    draft.isMessageUpdating = false;
  });
};
